import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import { LuScale, LuExternalLink } from "react-icons/lu";
import { reportEventBus } from "@dd-report-components/report/ReportEvents";
import { BOE_SEARCH_URL } from "@dd-report-components/report/Norms";

// The norm table, `normUrl`/`isNorma` and the `openNorma` emitter live in the
// shared report package: BlockRenderer auto-links norms from body text and
// ships with both interfaces. Only this modal — the thing that *shows* the
// norm, and only user-interface mounts it — stays here.
export { normUrl, isNorma, openNorma } from "@dd-report-components/report/Norms";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pop = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.97); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1350;
  background: rgba(20, 50, 59, 0.28);
  animation: ${fadeIn} 0.16s ease;
`;

const Dialog = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1351;
  width: min(25rem, 92vw);
  background: #ffffff;
  border: 1px solid #e6ece9;
  border-radius: 1rem;
  box-shadow: 0 20px 50px rgba(20, 50, 59, 0.22);
  animation: ${pop} 0.2s cubic-bezier(0.2, 0.7, 0.2, 1);
  overflow: hidden;
`;

const DialogBody = styled.div`
  padding: 1.25rem 1.375rem 1.25rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const Overline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #2c8c5e;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid #eef1ef;
  border-radius: 0.5rem;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa7ac;
`;

const Label = styled.div`
  margin-top: 0.8125rem;
  font-family: "Figtree", "Host Grotesk", sans-serif;
  font-size: 1.125rem;
  font-weight: 800;
  color: #14323b;
  letter-spacing: -0.02em;
  line-height: 1.25;
`;

const Excerpt = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: #6a7a80;
  line-height: 1.55;
`;

const BoeLink = styled.a`
  margin-top: 1.125rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 0.9375rem;
  background: #eaf5ef;
  border: 1px solid #d4eadd;
  border-radius: 0.6875rem;
  text-decoration: none;
  color: #237a50;
  font-family: "Figtree", "Host Grotesk", sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
`;

const BoeLinkLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

interface NormaState {
  label: string;
  url: string | null;
  // The excerpt, when the caller has one. Nothing produces it yet, so the
  // modal usually goes straight from the title to the BOE link.
  text?: string;
}

// Mounted once per report page, next to <CitationSidePanel>. Any recognized
// norm reference in report body text (rendered by BlockRenderer's legal-figure
// highlighting) can trigger it via `openNorma()` — no per-block wiring needed.
export const NormaModal = () => {
  const { t } = useTranslation();
  const [norma, setNorma] = useState<NormaState | null>(null);

  useEffect(() => {
    const onOpen = (n: NormaState) => setNorma(n);
    reportEventBus.on("REPORT_OPEN_NORMA", onOpen);
    return () => {
      reportEventBus.off("REPORT_OPEN_NORMA", onOpen);
    };
  }, []);

  useEffect(() => {
    if (!norma) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setNorma(null);
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [norma]);

  if (!norma) return null;

  const close = () => setNorma(null);
  const href = norma.url || BOE_SEARCH_URL;
  const linkLabel = norma.url
    ? t("dueDiligence.finalReport.normaModal.linkLabelOpen")
    : t("dueDiligence.finalReport.normaModal.linkLabelSearch");

  return (
    <>
      <Overlay onClick={close} />
      <Dialog role="dialog" aria-modal="true" aria-label={t("dueDiligence.finalReport.normaModal.ariaLabel")}>
        <DialogBody>
          <HeaderRow>
            <Overline>
              <LuScale size={14} />
              {t("dueDiligence.finalReport.normaModal.overline")}
            </Overline>
            <CloseButton onClick={close} aria-label={t("dueDiligence.finalReport.normaModal.close")}>
              <FiX size={16} />
            </CloseButton>
          </HeaderRow>
          <Label>{norma.label}</Label>
          {/* Only a real excerpt, never a placeholder: the old fallback
              ("Aquí se verá el extracto de la normativa y el enlace", still in
              i18n as normaModal.placeholderText) read as if something had
              failed to load, when the link below was all there ever was. */}
          {norma.text && <Excerpt>{norma.text}</Excerpt>}
          <BoeLink href={href} target="_blank" rel="noopener noreferrer">
            <LuExternalLink size={16} />
            <BoeLinkLabel>{linkLabel}</BoeLinkLabel>
          </BoeLink>
        </DialogBody>
      </Dialog>
    </>
  );
};
